// abi parameter definitions for otim build instruction action arguments
// @see https://abitype.dev for more information about abi typing

import type { AbiParameter } from "abitype";

import { InstructionAction } from "@generated/instruction-actions";

const targetField = {
  name: "target",
  type: "address",
} as const satisfies AbiParameter;

const scheduleField = {
  name: "schedule",
  type: "tuple",
  components: [
    { name: "startAt", type: "uint256" },
    { name: "startBy", type: "uint256" },
    { name: "interval", type: "uint256" },
    { name: "timeout", type: "uint256" },
  ],
} as const satisfies AbiParameter;

const tokenField = {
  name: "token",
  type: "address",
} as const satisfies AbiParameter;

const feeField = {
  name: "fee",
  type: "tuple",
  components: [
    { name: "token", type: "address" },
    { name: "maxBaseFeePerGas", type: "uint256" },
    { name: "maxPriorityFeePerGas", type: "uint256" },
    { name: "executionFee", type: "uint256" },
  ],
} as const satisfies AbiParameter;

// instruction abi parameter definitions
// each export represents the abi parameters for a specific instruction action

export const refuelArgumentsAbi = [
  targetField,
  { name: "threshold", type: "uint256" },
  { name: "endBalance", type: "uint256" },
  { name: "gasLimit", type: "uint256" },
  feeField,
] as const satisfies readonly AbiParameter[];

export const refuelERC20ArgumentsAbi = [
  tokenField,
  targetField,
  { name: "threshold", type: "uint256" },
  { name: "endBalance", type: "uint256" },
  feeField,
] as const satisfies readonly AbiParameter[];

export const transferArgumentsAbi = [
  targetField,
  { name: "value", type: "uint256" },
  { name: "gasLimit", type: "uint256" },
  scheduleField,
  feeField,
] as const satisfies readonly AbiParameter[];

export const transferERC20ArgumentsAbi = [
  tokenField,
  targetField,
  { name: "value", type: "uint256" },
  scheduleField,
  feeField,
] as const satisfies readonly AbiParameter[];

export const uniswapV3ExactInputArgumentsAbi = [
  { name: "recipient", type: "address" },
  { name: "tokenIn", type: "address" },
  { name: "tokenOut", type: "address" },
  { name: "feeTier", type: "uint24" },
  { name: "amountIn", type: "uint256" },
  { name: "floorAmountOut", type: "uint256" },
  { name: "meanPriceLookBack", type: "uint32" },
  { name: "maxPriceDeviationBPS", type: "uint32" },
  scheduleField,
  feeField,
] as const satisfies readonly AbiParameter[];

export const sweepDepositAccountArgumentsAbi = [
  { name: "depositor", type: "address" },
  { name: "recipient", type: "address" },
  { name: "threshold", type: "uint256" },
  feeField,
] as const satisfies readonly AbiParameter[];

export const sweepDepositAccountERC20ArgumentsAbi = [
  tokenField,
  { name: "depositor", type: "address" },
  { name: "recipient", type: "address" },
  { name: "threshold", type: "uint256" },
  feeField,
] as const satisfies readonly AbiParameter[];

export const sweepSkipCCTPDepositAccountArgumentsAbi = [
  { name: "depositor", type: "address" },
  { name: "destinationDomain", type: "uint32" },
  { name: "destinationMintRecipient", type: "bytes32" },
  { name: "threshold", type: "uint256" },
  feeField,
] as const satisfies readonly AbiParameter[];

export const deactivateInstructionArgumentsAbi = [
  { name: "instructionId", type: "bytes32" },
  feeField,
] as const satisfies readonly AbiParameter[];

// map of instruction action to its abi parameters
export const buildInstructionActionArgumentsAbiMap = {
  [InstructionAction.Refuel]: refuelArgumentsAbi,
  [InstructionAction.RefuelERC20]: refuelERC20ArgumentsAbi,
  [InstructionAction.Transfer]: transferArgumentsAbi,
  [InstructionAction.TransferERC20]: transferERC20ArgumentsAbi,
  [InstructionAction.UniswapV3ExactInput]: uniswapV3ExactInputArgumentsAbi,
  [InstructionAction.SweepDepositAccount]: sweepDepositAccountArgumentsAbi,
  [InstructionAction.SweepDepositAccountERC20]:
    sweepDepositAccountERC20ArgumentsAbi,
  [InstructionAction.SweepSkipCCTPDepositAccount]:
    sweepSkipCCTPDepositAccountArgumentsAbi,
  [InstructionAction.DeactivateInstruction]: deactivateInstructionArgumentsAbi,
} as const;
